import React, { useState, useMemo } from "react";
import ReactDOM from "react-dom";

const formatEmaDate = (dateStr) => {
    if (!dateStr) return "—";
    try {
        const d = new Date(dateStr);
        if (isNaN(d.getTime())) return dateStr;
        return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
    } catch {
        return dateStr;
    }
};

// Collapsible section shell — mirrors FDA's blue accordion panels
const Section = ({ title, defaultOpen = false, children }) => {
    const [open, setOpen] = useState(defaultOpen);
    return (
        <div className="discovery-section mb-2">
            <button
                type="button"
                className="discovery-section-header"
                onClick={() => setOpen((o) => !o)}
            >
                <span className="ev-small-p-dark ev-bold-med text-truncate">{title}</span>
                <i className={`bi ${open ? "bi-chevron-up" : "bi-chevron-down"} ev-fs-11 flex-shrink-0`} />
            </button>
            {open && <div className="discovery-section-body">{children}</div>}
        </div>
    );
};

const DocLinks = ({ docs, onPreview }) => {
    if (!docs || docs.length === 0) return <span className="ev-xsmall-p-dark text-muted">No documents available</span>;
    return (
        <div className="d-flex flex-column gap-1">
            {docs.map((d) => (
                <div key={d.document_id} className="d-flex align-items-center gap-2">
                    <span className="ev-small-p-blue discovery-doc-type">{d.name || "Document"} (PDF)</span>
                    <button
                        type="button"
                        className="ev-btn-lightblue-fill-round ev-btn-xsmall d-flex align-items-center gap-1"
                        onClick={() => onPreview(d.document_url, d.name)}
                    >
                        View <i className="bi bi-eye" />
                    </button>
                </div>
            ))}
        </div>
    );
};

const EMAMedicineDetailPanel = ({ medicine }) => {
    const [previewUrl, setPreviewUrl]   = useState(null);
    const [previewName, setPreviewName] = useState("");

    // ── Format a raw type slug (e.g. "assessment-report") into a readable heading ──
    const formatDocTypeLabel = (type) => {
        if (!type) return "Other Documents";
        return type
            .replace(/[-_]/g, " ")
            .split(" ")
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(" ");
    };

    // ── Group documents by their real EPAR `type` value — data-driven, no hardcoded buckets ──
    const documentGroups = useMemo(() => {
        const docs = medicine?.documents || [];
        return docs.reduce((groups, doc) => {
            const key = formatDocTypeLabel(doc.type);
            groups[key] = groups[key] || [];
            groups[key].push(doc);
            return groups;
        }, {});
    }, [medicine]);

    if (!medicine) {
        return (
            <div className="ev-card-2 p-4 vh-55 d-flex flex-column align-items-center justify-content-center">
                <i className="bi bi-capsule ev-fs-50 text-muted mb-3"></i>
                <p className="ev-reg-p-dark ev-bold-med mb-1">No Medicine Selected</p>
                <p className="ev-small-p-dark text-muted text-center mb-0">
                    Select a medicine from the list to view its details.
                </p>
            </div>
        );
    }

    const statusBadgeClass = {
        "Authorised": "discovery-badge-green",
        "Withdrawn":  "discovery-badge-orange",
        "Refused":    "discovery-badge-red",
        "Suspended":  "discovery-badge-orange",
        "Revoked":    "discovery-badge-orange",
    }[medicine.medicine_status] || "discovery-badge-neutral";

    return (
        <div className="ev-card-2 p-3 pe-1 vh-55 d-flex flex-column position-relative">
            {/* Fixed header */}
            <div className="flex-shrink-0 pe-2">
                <div className="d-flex align-items-center gap-2 mb-1 flex-wrap">
                    <p className="ev-bold-med ev-reg-p-dark mb-0">{medicine.name_of_medicine || "—"}</p>
                    {medicine.medicine_status && (
                        <span className={statusBadgeClass} style={{ width: "auto" }}>{medicine.medicine_status}</span>
                    )}
                    {medicine.orphan && <span className="ev-badge-pill ev-fs-11 ev-badge-purple">Orphan</span>}
                    {medicine.biosimilar && <span className="ev-badge-pill ev-fs-11 ev-badge-blue">Biosimilar</span>}
                    {medicine.generic && <span className="ev-badge-pill ev-fs-11 ev-badge-light">Generic</span>}
                </div>
                <p className="ev-xsmall-p-dark text-muted mb-2">
                    EMA Product Number: {medicine.ema_product_number || "—"}
                </p>
            </div>

            {/* Scrollable sections */}
            <div className="flex-grow-1 scrollable-element pe-2">

                {/* ── Overview ── */}
                <Section title="Overview" defaultOpen={true}>
                    <div className="row g-2">
                        <div className="col-6">
                            <p className="ev-xsmall-p-dark text-muted mb-0">Active Substance</p>
                            <p className="ev-small-p-dark ev-bold-med mb-0">{medicine.active_substance || "—"}</p>
                        </div>
                        <div className="col-6">
                            <p className="ev-xsmall-p-dark text-muted mb-0">INN</p>
                            <p className="ev-small-p-dark ev-bold-med mb-0">{medicine.inn || "—"}</p>
                        </div>
                        <div className="col-6">
                            <p className="ev-xsmall-p-dark text-muted mb-0">Therapeutic Area</p>
                            <p className="ev-small-p-dark ev-bold-med mb-0">{medicine.therapeutic_area_mesh || "—"}</p>
                        </div>
                        <div className="col-6">
                            <p className="ev-xsmall-p-dark text-muted mb-0">ATC Code</p>
                            <p className="ev-small-p-dark ev-bold-med mb-0">{medicine.atc_code_human || "—"}</p>
                        </div>
                        <div className="col-12">
                            <p className="ev-xsmall-p-dark text-muted mb-0">Therapeutic Indication</p>
                            <p className="ev-small-p-dark mb-0">{medicine.therapeutic_indication || "—"}</p>
                        </div>
                    </div>
                </Section>

                {/* ── Authorisation Details ── */}
                <Section title="Authorisation Details">
                    <div className="row g-2">
                        <div className="col-6">
                            <p className="ev-xsmall-p-dark text-muted mb-0">Marketing Authorisation Holder</p>
                            <p className="ev-small-p-dark ev-bold-med mb-0">{medicine.marketing_authorisation_holder || "—"}</p>
                        </div>
                        <div className="col-6">
                            <p className="ev-xsmall-p-dark text-muted mb-0">Decision Date</p>
                            <p className="ev-small-p-dark ev-bold-med mb-0">{formatEmaDate(medicine.decision_date)}</p>
                        </div>
                        <div className="col-6">
                            <p className="ev-xsmall-p-dark text-muted mb-0">Revision Number</p>
                            <p className="ev-small-p-dark ev-bold-med mb-0">{medicine.revision_number ?? "—"}</p>
                        </div>
                        <div className="col-6">
                            <p className="ev-xsmall-p-dark text-muted mb-0">Last Updated</p>
                            <p className="ev-small-p-dark ev-bold-med mb-0">{formatEmaDate(medicine.last_updated_date)}</p>
                        </div>
                    </div>
                    {medicine.medicine_url && (
                        <div className="mt-2">
                            <button
                                type="button"
                                className="ev-btn-lightblue-fill-round ev-btn-xsmall d-flex align-items-center gap-1"
                                onClick={() => window.open(medicine.medicine_url, "_blank", "noopener,noreferrer")}
                            >
                                View on EMA Website <i className="bi bi-box-arrow-up-right" />
                            </button>
                        </div>
                    )}
                </Section>

                {/* ── Documents — grouped dynamically by their real EPAR type ── */}
                {Object.keys(documentGroups).length === 0 ? (
                    <Section title="Documents">
                        <p className="ev-xsmall-p-dark text-muted mb-0">No documents available on this site.</p>
                    </Section>
                ) : (
                    Object.entries(documentGroups).map(([type, docs]) => (
                        <Section key={type} title={type}>
                            <DocLinks
                                docs={docs}
                                onPreview={(url, name) => { setPreviewUrl(url); setPreviewName(name); }}
                            />
                        </Section>
                    ))
                )}
            </div>

            {!!previewUrl && ReactDOM.createPortal(
                <div className="modal d-block bg-dark bg-opacity-50" style={{ zIndex: "var(--num-1000000000)" }} onClick={(e) => e.stopPropagation()}>
                    <div className="modal-dialog modal-dialog-centered modal-lg modal-dialog-scrollable">
                        <div className="modal-content ev-card-3">
                            <div className="row modal-header border-0">
                                <div className="col-10">
                                    <h6 className="ev-message-card3 m-0">{previewName || "Document Preview"}</h6>
                                </div>
                                <div className="col-2 text-end d-flex align-items-center justify-content-end gap-2">
                                    <button
                                        type="button"
                                        className="btn ev-btn-lightblue-fill-round ev-radius-xsmall ev-btn-med avatar-tooltip"
                                        data-tooltip="Open in New Tab"
                                        onClick={() => window.open(previewUrl, "_blank", "noopener,noreferrer")}
                                    >
                                        <i className="bi bi-box-arrow-up-right"></i>
                                    </button>
                                    <button type="button" className="ev-btn-text-only-fill ev-fs-20" onClick={() => { setPreviewUrl(null); setPreviewName(""); }}>
                                        <i className="bi bi-x"></i>
                                    </button>
                                </div>
                            </div>
                            <div className="modal-body p-0 ev-radius-small vh-80">
                                <iframe
                                    src={`https://docs.google.com/viewer?url=${encodeURIComponent(previewUrl)}&embedded=true`}
                                    title={previewName || "Document Preview"}
                                    width="100%"
                                    height="100%"
                                    style={{ border: "none", minHeight: "75vh" }}
                                />
                            </div>
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
};

export default EMAMedicineDetailPanel;